// frontend/src/pages/employee/EmployeeForum.jsx
// Anonymous Employee Forum - FIXED ESLint errors

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const EmployeeForum = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [showPostModal, setShowPostModal] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('');
    const [sortBy, setSortBy] = useState('recent');
    const [currentUser, setCurrentUser] = useState(null);

    // Admin edit states
    const [isEditingPost, setIsEditingPost] = useState(false);
    const [editPostData, setEditPostData] = useState({});
    const [editingReplyId, setEditingReplyId] = useState(null);
    const [editReplyContent, setEditReplyContent] = useState('');

    const [newPost, setNewPost] = useState({
        content: '',
        category: 'other',
        tags: [],
        isAnonymous: true
    });

    const [replyContent, setReplyContent] = useState('');
    const [isAIChat, setIsAIChat] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const categories = [
        { value: 'work_life_balance', label: '⚖️ Work-Life Balance' },
        { value: 'health_wellness', label: '💪 Health & Wellness' },
        { value: 'financial', label: '💰 Financial Advice' },
        { value: 'relationships', label: '❤️ Relationships' },
        { value: 'career', label: '🎯 Career Growth' },
        { value: 'mental_health', label: '🧠 Mental Health' },
        { value: 'productivity', label: '⚡ Productivity' },
        { value: 'learning', label: '📚 Learning & Skills' },
        { value: 'lifestyle', label: '🌟 Lifestyle' },
        { value: 'other', label: '💭 Other' }
    ];

    useEffect(() => {
        fetchCurrentUser();
        fetchPosts();
    }, [categoryFilter, sortBy]);

    const fetchCurrentUser = async () => {
        try {
            const response = await api.get('/auth/me');
            setCurrentUser(response.data.user);

            //console.log("FULL USER RESPONSE:", response.data);
            //console.log("USER DATA:", response.data.data);

            //setCurrentUser(response.data.data);
        } catch (error) {
            console.error('Fetch user error:', error);
        }
    };

    const isAdmin = () => {
        const roleName = currentUser?.role?.name;

        //console.log("Current User Role:", roleName);
        //console.log("Lowercase Role:", roleName?.toLowerCase());

        const result = roleName?.toLowerCase() === 'admin' ||
            roleName?.toLowerCase() === 'super_admin';

        //console.log("Is Admin:", result);

        return result;
    };

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const params = { sortBy };
            if (categoryFilter) params.category = categoryFilter;

            const response = await api.get('/forum', { params });
            setPosts(response.data.data);
        } catch (error) {
            console.error('Fetch posts error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePost = async (e) => {
        e.preventDefault();

        if (!newPost.content.trim()) {
            alert('Please write something');
            return;
        }

        try {
            await api.post('/forum', newPost);
            setShowCreateModal(false);
            setNewPost({
                content: '',
                category: 'other',
                tags: [],
                isAnonymous: true
            });
            fetchPosts();
        } catch (error) {
            console.error('Create post error:', error);
            alert('Failed to create post');
        }
    };

    const viewPost = async (postId) => {
        try {
            const response = await api.get(`/forum/${postId}`);
            setSelectedPost(response.data.data);
            setEditPostData({
                content: response.data.data.content,
                category: response.data.data.category,
                isPinned: response.data.data.isPinned,
                isSolved: response.data.data.isSolved
            });
            setShowPostModal(true);
            setIsEditingPost(false);
        } catch (error) {
            console.error('Fetch post error:', error);
            alert('Failed to load post');
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();

        if (!replyContent.trim()) {
            alert('Please write a reply');
            return;
        }

        try {
            if (replyContent.trim().startsWith('/AI')) {
                setAiLoading(true);
            }

            await api.post(`/forum/${selectedPost._id}/reply`, {
                content: replyContent,
                isAnonymous: true
            });

            setReplyContent('');
            setIsAIChat(false);

            // Refresh post
            const response = await api.get(`/forum/${selectedPost._id}`);
            setSelectedPost(response.data.data);

            setAiLoading(false);
        } catch (error) {
            console.error('Reply error:', error);
            alert(error.response?.data?.message || 'Failed to add reply');
            setAiLoading(false);
        }
    };

    const handleUpvote = async (postId) => {
        try {
            await api.post(`/forum/${postId}/upvote`);
            fetchPosts();
            if (selectedPost && selectedPost._id === postId) {
                const response = await api.get(`/forum/${postId}`);
                setSelectedPost(response.data.data);
            }
        } catch (error) {
            console.error('Upvote error:', error);
        }
    };

    // ADMIN FUNCTIONS

    const handleEditPost = async () => {
        try {
            console.log("Updating post:", editPostData);

            await api.put(`/forum/${selectedPost._id}`, editPostData);

            alert('Post updated successfully!');
            setIsEditingPost(false);

            // ✅ Refresh BOTH
            const response = await api.get(`/forum/${selectedPost._id}`);
            setSelectedPost(response.data.data);

            fetchPosts(); // 👈 THIS is what updates list for everyone view

        } catch (error) {
            console.error('Update post error:', error);
            alert('Failed to update post');
        }
    };

    const handleDeletePost = async (postId) => {
        // FIXED: Use window.confirm instead of confirm
        if (!window.confirm('Are you sure you want to hide this post?')) return;

        try {
            await api.delete(`/forum/${postId}`);
            alert('Post hidden successfully!');
            setShowPostModal(false);
            fetchPosts();
        } catch (error) {
            console.error('Delete post error:', error);
            alert('Failed to delete post');
        }
    };

    const handleEditReply = async (replyId) => {
        if (!editReplyContent.trim()) {
            alert('Reply content cannot be empty');
            return;
        }

        try {
            await api.put(`/forum/${selectedPost._id}/reply/${replyId}`, {
                content: editReplyContent
            });

            alert('Reply updated successfully!');
            setEditingReplyId(null);
            setEditReplyContent('');

            // Refresh post
            const response = await api.get(`/forum/${selectedPost._id}`);
            setSelectedPost(response.data.data);
        } catch (error) {
            console.error('Update reply error:', error);
            alert('Failed to update reply');
        }
    };

    const handleDeleteReply = async (replyId) => {
        // FIXED: Use window.confirm instead of confirm
        if (!window.confirm('Are you sure you want to delete this reply?')) return;

        try {
            await api.delete(`/forum/${selectedPost._id}/reply/${replyId}`);
            alert('Reply deleted successfully!');

            // Refresh post
            const response = await api.get(`/forum/${selectedPost._id}`);
            setSelectedPost(response.data.data);
        } catch (error) {
            console.error('Delete reply error:', error);
            alert('Failed to delete reply');
        }
    };

    const togglePin = () => {
        setEditPostData(prev => ({ ...prev, isPinned: !prev.isPinned }));
    };

    const toggleSolved = () => {
        setEditPostData(prev => ({ ...prev, isSolved: !prev.isSolved }));
    };

    const getCategoryIcon = (category) => {
        const cat = categories.find(c => c.value === category);
        return cat ? cat.label : '💭 Other';
    };

    const formatTimeAgo = (date) => {
        const seconds = Math.floor((new Date() - new Date(date)) / 1000);

        let interval = seconds / 31536000;
        if (interval > 1) return Math.floor(interval) + ' years ago';

        interval = seconds / 2592000;
        if (interval > 1) return Math.floor(interval) + ' months ago';

        interval = seconds / 86400;
        if (interval > 1) return Math.floor(interval) + ' days ago';

        interval = seconds / 3600;
        if (interval > 1) return Math.floor(interval) + ' hours ago';

        interval = seconds / 60;
        if (interval > 1) return Math.floor(interval) + ' minutes ago';

        return 'Just now';
    };

    const getAvatarColor = (hash) => {
        if (hash === 'ai-assistant') return 'bg-purple-500';

        const colors = [
            'bg-blue-500',
            'bg-green-500',
            'bg-yellow-500',
            'bg-red-500',
            'bg-indigo-500',
            'bg-pink-500',
            'bg-teal-500'
        ];

        const index = parseInt(hash.substring(0, 2), 16) % colors.length;
        return colors[index];
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">
                                Employee Support Forum
                            </h1>
                            <p className="text-gray-600 mb-4">
                                Share your daily life problems anonymously. Get support from colleagues or chat with AI using "/AI"
                            </p>
                        </div>
                        {isAdmin() && (
                            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                                Admin
                            </span>
                        )}
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                        <p className="text-sm text-blue-800">
                            💡 <strong>How to use AI Chat:</strong> Type "/AI" followed by your question in any reply.
                            Example: "/AI How do I manage work stress?"
                        </p>
                    </div>

                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition font-semibold"
                    >
                        + Share Your Problem (Anonymous)
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                    <div className="flex flex-wrap gap-4">
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>

                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="recent">Recent</option>
                            <option value="popular">Popular</option>
                            <option value="discussed">Most Discussed</option>
                        </select>
                    </div>
                </div>

                {/* Posts */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow-md">
                        <p className="text-gray-500">No posts yet. Be the first to share!</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {posts.map(post => (
                            <div
                                key={post._id}
                                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition cursor-pointer"
                                onClick={() => viewPost(post._id)}
                            >
                                {/* Header */}
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full ${getAvatarColor(post.authorHash)} flex items-center justify-center text-white font-bold`}>
                                            A
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-800">Anonymous</p>
                                            <p className="text-sm text-gray-500">{formatTimeAgo(post.createdAt)}</p>
                                        </div>
                                    </div>
                                    <span className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                                        {getCategoryIcon(post.category)}
                                    </span>
                                </div>

                                {/* Content */}
                                <p className="text-gray-800 mb-4 line-clamp-3">{post.content}</p>

                                {/* Tags */}
                                {post.tags && post.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {post.tags.map((tag, idx) => (
                                            <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleUpvote(post._id);
                                        }}
                                        className="flex items-center gap-1 hover:text-blue-600"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                        {post.upvotes}
                                    </button>

                                    <span className="flex items-center gap-1">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                        {post.replies.length} replies
                                    </span>

                                    <span className="flex items-center gap-1">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        {post.viewCount}
                                    </span>

                                    {post.isPinned && (
                                        <span className="text-yellow-600 font-semibold">📌 Pinned</span>
                                    )}

                                    {post.isSolved && (
                                        <span className="text-green-600 font-semibold">✓ Solved</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Create Post Modal */}
                {showCreateModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <h2 className="text-2xl font-bold mb-4">Share Your Problem</h2>
                            <p className="text-gray-600 mb-6">
                                Your identity will remain 100% anonymous. Share freely.
                            </p>

                            <form onSubmit={handleCreatePost}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category
                                    </label>
                                    <select
                                        value={newPost.category}
                                        onChange={(e) => setNewPost(prev => ({ ...prev, category: e.target.value }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        {categories.map(cat => (
                                            <option key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        What's on your mind?
                                    </label>
                                    <textarea
                                        value={newPost.content}
                                        onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        rows="6"
                                        placeholder="Share your problem, concern, or what you're going through..."
                                        required
                                    />
                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tags (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="stress, anxiety, time-management (comma separated)"
                                        onChange={(e) => setNewPost(prev => ({
                                            ...prev,
                                            tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                                        }))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateModal(false)}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                                    >
                                        Post Anonymously
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* View Post Modal WITH ADMIN CONTROLS */}
                {showPostModal && selectedPost && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-start mb-6">
                                <h2 className="text-2xl font-bold">Discussion</h2>
                                <div className="flex items-center gap-2">
                                    {isAdmin() && (
                                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                                            Admin Mode
                                        </span>
                                    )}
                                    <button
                                        onClick={() => {
                                            setShowPostModal(false);
                                            setSelectedPost(null);
                                            setReplyContent('');
                                            setIsEditingPost(false);
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* ADMIN CONTROLS FOR POST */}
                            {isAdmin() && !isEditingPost && (
                                <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                                    <h3 className="font-semibold text-red-800 mb-3">Admin Controls</h3>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => setIsEditingPost(true)}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                                        >
                                            ✏️ Edit Post
                                        </button>
                                        <button
                                            onClick={() => handleDeletePost(selectedPost._id)}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                                        >
                                            🗑️ Delete Post
                                        </button>
                                        <button
                                            onClick={async () => {
                                                const updatedData = {
                                                    ...editPostData,
                                                    isPinned: !editPostData.isPinned
                                                };

                                                console.log("Sending PIN update:", updatedData);

                                                setEditPostData(updatedData);

                                                try {
                                                    await api.put(`/forum/${selectedPost._id}`, updatedData);

                                                    fetchPosts();

                                                    const response = await api.get(`/forum/${selectedPost._id}`);
                                                    setSelectedPost(response.data.data);

                                                } catch (err) {
                                                    console.error(err);
                                                }
                                            }}
                                            className={`px-4 py-2 rounded-lg text-sm ${selectedPost.isPinned
                                                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                }`}
                                        >
                                            📌 {selectedPost.isPinned ? 'Unpin' : 'Pin'}
                                        </button>
                                        <button
                                            onClick={async () => {
                                                const updatedData = {
                                                    ...editPostData,
                                                    isSolved: !editPostData.isSolved
                                                };

                                                console.log("Sending to API:", updatedData);

                                                setEditPostData(updatedData); // update UI

                                                try {
                                                    await api.put(`/forum/${selectedPost._id}`, updatedData);

                                                    fetchPosts();

                                                    const response = await api.get(`/forum/${selectedPost._id}`);
                                                    setSelectedPost(response.data.data);

                                                } catch (err) {
                                                    console.error(err);
                                                }
                                            }}
                                            className={`px-4 py-2 rounded-lg text-sm ${selectedPost.isSolved
                                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                }`}
                                        >
                                            ✓ {selectedPost.isSolved ? 'Mark Unsolved' : 'Mark Solved'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* EDIT POST MODE */}
                            {isAdmin() && isEditingPost ? (
                                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                                    <h3 className="font-semibold text-blue-800 mb-3">Edit Post</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Content</label>
                                            <textarea
                                                value={editPostData.content}
                                                onChange={(e) => setEditPostData(prev => ({ ...prev, content: e.target.value }))}
                                                className="w-full px-4 py-2 border rounded-lg"
                                                rows="4"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Category</label>
                                            <select
                                                value={editPostData.category}
                                                onChange={(e) => setEditPostData(prev => ({ ...prev, category: e.target.value }))}
                                                className="w-full px-4 py-2 border rounded-lg"
                                            >
                                                {categories.map(cat => (
                                                    <option key={cat.value} value={cat.value}>
                                                        {cat.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={handleEditPost}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                            >
                                                Save Changes
                                            </button>
                                            <button
                                                onClick={() => setIsEditingPost(false)}
                                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Original Post */
                                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className={`w-10 h-10 rounded-full ${getAvatarColor(selectedPost.authorHash)} flex items-center justify-center text-white font-bold`}>
                                            A
                                        </div>
                                        <div>
                                            <p className="font-semibold">Anonymous</p>
                                            <p className="text-sm text-gray-500">{formatTimeAgo(selectedPost.createdAt)}</p>
                                        </div>
                                        <span className="ml-auto text-sm px-3 py-1 bg-white rounded-full">
                                            {getCategoryIcon(selectedPost.category)}
                                        </span>
                                    </div>
                                    <p className="text-gray-800 whitespace-pre-wrap">{selectedPost.content}</p>

                                    {/* Upvote in modal */}
                                    <div className="mt-4 flex items-center gap-2">
                                        <button
                                            onClick={() => handleUpvote(selectedPost._id)}
                                            className="flex items-center gap-1 px-3 py-1 bg-white rounded-full hover:bg-gray-100 text-sm"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                            </svg>
                                            {selectedPost.upvotes} upvotes
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Replies */}
                            <div className="mb-6">
                                <h3 className="font-semibold text-gray-800 mb-4">
                                    Replies ({selectedPost.replies.length})
                                </h3>
                                <div className="space-y-4">
                                    {selectedPost.replies.map((reply, idx) => (
                                        <div key={reply._id || idx} className={`p-4 rounded-lg ${reply.isAI ? 'bg-purple-50 border-l-4 border-purple-500' : 'bg-gray-50'}`}>
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full ${getAvatarColor(reply.authorHash)} flex items-center justify-center text-white text-sm font-bold`}>
                                                        {reply.isAI ? '🤖' : 'A'}
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-sm">
                                                            {reply.isAI ? `AI Assistant (${reply.aiModel})` : 'Anonymous'}
                                                        </p>
                                                        <p className="text-xs text-gray-500">{formatTimeAgo(reply.createdAt)}</p>
                                                    </div>
                                                </div>

                                                {/* ADMIN CONTROLS FOR REPLY */}
                                                {isAdmin() && !reply.isAI && (
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setEditingReplyId(reply._id);
                                                                setEditReplyContent(reply.content);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 text-xs"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteReply(reply._id)}
                                                            className="text-red-600 hover:text-red-800 text-xs"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* EDIT REPLY MODE */}
                                            {editingReplyId === reply._id ? (
                                                <div className="mt-2">
                                                    <textarea
                                                        value={editReplyContent}
                                                        onChange={(e) => setEditReplyContent(e.target.value)}
                                                        className="w-full px-3 py-2 border rounded-lg text-sm"
                                                        rows="3"
                                                    />
                                                    <div className="flex gap-2 mt-2">
                                                        <button
                                                            onClick={() => handleEditReply(reply._id)}
                                                            className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditingReplyId(null);
                                                                setEditReplyContent('');
                                                            }}
                                                            className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-gray-800 text-sm whitespace-pre-wrap">{reply.content}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Reply Form */}
                            <form onSubmit={handleReply}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Your Reply {isAIChat && '(Chat with AI 🤖)'}
                                    </label>
                                    <textarea
                                        value={replyContent}
                                        onChange={(e) => {
                                            setReplyContent(e.target.value);
                                            setIsAIChat(e.target.value.trim().startsWith('/AI'));
                                        }}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        rows="4"
                                        placeholder="Type your reply or start with '/AI' to chat with AI assistant..."
                                        disabled={aiLoading}
                                    />
                                    {isAIChat && (
                                        <p className="mt-2 text-sm text-purple-600">
                                            💡 AI will provide supportive advice based on the original post and your question
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    disabled={aiLoading}
                                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    {aiLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            AI is thinking...
                                        </span>
                                    ) : isAIChat ? (
                                        'Ask AI 🤖'
                                    ) : (
                                        'Post Reply'
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeForum;